import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PermissionModule = 'candidates' | 'jobs' | 'companies' | 'clients' | 'settings' | 'admin' | 'skills' | 'notifications';
export type PermissionAction = 'create' | 'read' | 'update' | 'delete';

interface RolePermission {
  module: PermissionModule;
  action: PermissionAction;
}

interface UserPermissions {
  roleIds: string[];
  roleNames: string[];
  permissions: RolePermission[];
}

export const usePermissions = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    roleIds: [],
    roleNames: [],
    permissions: []
  });
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // First get the app_user record
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id, role')
        .eq('user_id', user.id)
        .single();

      if (!appUser) {
        setLoading(false);
        return;
      }

      // Check if user is a base admin (from app_users.role)
      const userIsAdmin = appUser.role === 'admin';
      setIsAdmin(userIsAdmin);

      const roleIds: string[] = [];
      const roleNames: string[] = [];
      const permissions: RolePermission[] = [];

      // Fetch user's custom roles and their permissions
      const { data: userRoles } = await supabase
        .from('user_custom_roles')
        .select(`
          role_id,
          custom_role:custom_roles(
            id,
            name,
            role_permissions(module, action)
          )
        `)
        .eq('user_id', appUser.id);

      if (userRoles && userRoles.length > 0) {
        userRoles.forEach((ur: any) => {
          if (ur.custom_role) {
            roleIds.push(ur.custom_role.id);
            roleNames.push(ur.custom_role.name);
            
            if (ur.custom_role.role_permissions) {
              ur.custom_role.role_permissions.forEach((perm: any) => {
                // Avoid duplicates
                if (!permissions.some(p => p.module === perm.module && p.action === perm.action)) {
                  permissions.push({
                    module: perm.module as PermissionModule,
                    action: perm.action as PermissionAction
                  });
                }
              });
            }
          }
        });
      }

      // If user has no custom roles but is a recruiter, fetch and apply default recruiter permissions
      if (roleIds.length === 0 && appUser.role === 'recruiter') {
        const { data: recruiterRole } = await supabase
          .from('custom_roles')
          .select(`
            id,
            name,
            role_permissions(module, action)
          `)
          .eq('name', 'Recruiter')
          .single();

        if (recruiterRole) {
          roleIds.push(recruiterRole.id);
          roleNames.push(recruiterRole.name);
          
          if (recruiterRole.role_permissions) {
            (recruiterRole.role_permissions as any[]).forEach((perm: any) => {
              if (!permissions.some(p => p.module === perm.module && p.action === perm.action)) {
                permissions.push({
                  module: perm.module as PermissionModule,
                  action: perm.action as PermissionAction
                });
              }
            });
          }
        }
      }

      setUserPermissions({ roleIds, roleNames, permissions });
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((module: PermissionModule, action: PermissionAction): boolean => {
    // Admins have all permissions
    if (isAdmin) return true;
    
    return userPermissions.permissions.some(
      p => p.module === module && p.action === action
    );
  }, [isAdmin, userPermissions.permissions]);

  // Check if user can perform any action on a module
  const canAccessModule = useCallback((module: PermissionModule): boolean => {
    if (isAdmin) return true;
    return userPermissions.permissions.some(p => p.module === module);
  }, [isAdmin, userPermissions.permissions]);

  // Check multiple permissions at once (returns true if user has ANY of them)
  const hasAnyPermission = useCallback((checks: Array<{ module: PermissionModule; action: PermissionAction }>): boolean => {
    if (isAdmin) return true;
    return checks.some(check => hasPermission(check.module, check.action));
  }, [isAdmin, hasPermission]);

  // Check multiple permissions at once (returns true if user has ALL of them)
  const hasAllPermissions = useCallback((checks: Array<{ module: PermissionModule; action: PermissionAction }>): boolean => {
    if (isAdmin) return true;
    return checks.every(check => hasPermission(check.module, check.action));
  }, [isAdmin, hasPermission]);

  // Convenience methods for common CRUD operations
  const can = useMemo(() => ({
    create: (module: PermissionModule) => hasPermission(module, 'create'),
    read: (module: PermissionModule) => hasPermission(module, 'read'),
    update: (module: PermissionModule) => hasPermission(module, 'update'),
    delete: (module: PermissionModule) => hasPermission(module, 'delete'),
  }), [hasPermission]);

  // Get all permissions for a specific module
  const getModulePermissions = useCallback((module: PermissionModule) => {
    if (isAdmin) {
      return { create: true, read: true, update: true, delete: true };
    }
    
    const modulePerms = userPermissions.permissions.filter(p => p.module === module);
    return {
      create: modulePerms.some(p => p.action === 'create'),
      read: modulePerms.some(p => p.action === 'read'),
      update: modulePerms.some(p => p.action === 'update'),
      delete: modulePerms.some(p => p.action === 'delete'),
    };
  }, [isAdmin, userPermissions.permissions]);

  return {
    loading,
    isAdmin,
    roles: userPermissions.roleNames,
    roleIds: userPermissions.roleIds,
    permissions: userPermissions.permissions,
    hasPermission,
    canAccessModule,
    hasAnyPermission,
    hasAllPermissions,
    can,
    getModulePermissions,
    refetch: fetchPermissions
  };
};

// Utility component to conditionally render based on permissions
interface PermissionGateProps {
  module: PermissionModule;
  action: PermissionAction;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const PermissionGate = ({ 
  module, 
  action, 
  children, 
  fallback = null 
}: PermissionGateProps) => {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return null;
  
  if (hasPermission(module, action)) {
    return children;
  }
  
  return fallback;
};
